import { useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import useSWR, { useSWRConfig } from 'swr';

import type {
  AccountAddress,
  AccountInquiry,
  AccountInvoice,
  AccountOrder,
  AccountProfile,
  AuthSession,
  CollectionDetailResponse,
  InquiryPayload,
  ProductDetailResponse,
  PublicCollection,
  PublicOffer,
  PublicProduct,
  PublicVariant,
  WishlistItem,
} from '@/types/commerce';
import { trackCommerceEvent } from '@lib/commerce-analytics';
import { buildQueryString, commerceApi, CommerceApiError, getCommerceErrorMessage } from '@lib/commerce-api';
import { buildCollectionInquiryMessage, buildProductInquiryMessage, buildWhatsAppHref } from '@lib/commerce-formatters';
import { addGuestWishlistItem, clearGuestWishlist, getGuestWishlist } from '@lib/guest-wishlist';

const SESSION_KEY = '/auth/session';

type AuthCredentials = {
  email: string;
  password: string;
};

type RegisterPayload = AuthCredentials & {
  name: string;
};

type OtpType = 'sign-in' | 'email-verification' | 'forget-password';

const swrOptions = {
  revalidateOnFocus: false,
  dedupingInterval: 15_000,
  shouldRetryOnError: false,
  errorRetryCount: 0,
};

const accountSwrOptions = {
  ...swrOptions,
};

export function useAuthSession() {
  return useSWR<AuthSession | null>(
    SESSION_KEY,
    async () => {
      try {
        return await commerceApi.get<AuthSession | null>(SESSION_KEY);
      } catch (error) {
        if (error instanceof CommerceApiError && error.status === 401) {
          return null;
        }
        throw error;
      }
    },
    swrOptions
  );
}

export function usePublicProducts(
  params: {
    q?: string;
    collection?: string;
    tag?: string;
    sort?: 'featured' | 'latest' | 'price-asc' | 'price-desc' | 'title';
  } = {}
) {
  return useSWR<{ data: PublicProduct[] }>(
    `/catalog/products${buildQueryString(params)}`,
    (path: string) => commerceApi.get(path),
    swrOptions
  );
}

export function usePublicProduct(slug?: string | null) {
  return useSWR<ProductDetailResponse>(
    slug ? `/catalog/products/${slug}` : null,
    (path: string) => commerceApi.get(path),
    swrOptions
  );
}

export function usePublicCollections() {
  return useSWR<{ data: PublicCollection[] }>(
    '/catalog/collections',
    (path: string) => commerceApi.get(path),
    swrOptions
  );
}

export function usePublicCollection(slug?: string | null) {
  return useSWR<CollectionDetailResponse>(
    slug ? `/catalog/collections/${slug}` : null,
    (path: string) => commerceApi.get(path),
    swrOptions
  );
}

export function usePublicSearch(query?: string | null) {
  return useSWR<{ data: PublicProduct[] }>(
    query?.trim() ? `/catalog/search${buildQueryString({ q: query.trim() })}` : null,
    (path: string) => commerceApi.get(path),
    swrOptions
  );
}

export function usePublicOffers(placement?: string | null) {
  return useSWR<{ data: PublicOffer[] }>(
    `/offers${buildQueryString({ placement: placement ?? undefined })}`,
    (path: string) => commerceApi.get(path),
    swrOptions
  );
}

export function usePublicOffer(slug?: string | null) {
  return useSWR<{ data: PublicOffer }>(
    slug ? `/offers/${slug}` : null,
    (path: string) => commerceApi.get(path),
    swrOptions
  );
}

export function useAccountProfile(enabled = true) {
  return useSWR<{ data: AccountProfile }>(
    enabled ? '/account/profile' : null,
    (path: string) => commerceApi.get(path),
    accountSwrOptions
  );
}

export function useAccountAddresses(enabled = true) {
  return useSWR<{ data: AccountAddress[] }>(
    enabled ? '/account/addresses' : null,
    (path: string) => commerceApi.get(path),
    accountSwrOptions
  );
}

export function useWishlist(enabled = true) {
  return useSWR<{ data: WishlistItem[] }>(
    enabled ? '/account/wishlist' : null,
    (path: string) => commerceApi.get(path),
    accountSwrOptions
  );
}

export function useAccountOrders(enabled = true) {
  return useSWR<{ data: AccountOrder[] }>(
    enabled ? '/account/orders' : null,
    (path: string) => commerceApi.get(path),
    accountSwrOptions
  );
}

export function useAccountOrder(id?: string | null, enabled = true) {
  return useSWR<{ data: AccountOrder }>(
    enabled && id ? `/account/orders/${id}` : null,
    (path: string) => commerceApi.get(path),
    accountSwrOptions
  );
}

export function useAccountInvoices(enabled = true) {
  return useSWR<{ data: AccountInvoice[] }>(
    enabled ? '/account/invoices' : null,
    (path: string) => commerceApi.get(path),
    accountSwrOptions
  );
}

export function useAccountInquiries(enabled = true) {
  return useSWR<{ data: AccountInquiry[] }>(
    enabled ? '/account/inquiries' : null,
    (path: string) => commerceApi.get(path),
    accountSwrOptions
  );
}

export function useGuestWishlist() {
  return useMemo(() => getGuestWishlist(), []);
}

export function useWishlistMerge(session: AuthSession | null | undefined) {
  const { mutate } = useSWRConfig();

  useEffect(() => {
    if (!session?.user) {
      return;
    }

    const guestItems = getGuestWishlist();
    if (!guestItems.length) {
      return;
    }

    let cancelled = false;

    const merge = async () => {
      for (const item of guestItems) {
        if (cancelled) {
          return;
        }

        await commerceApi.post<{ success: true }>('/account/wishlist', item);
      }

      clearGuestWishlist();
      await mutate('/account/wishlist');
    };

    void merge().catch((error) => {
      toast.error(getCommerceErrorMessage(error, 'Unable to sync saved items right now.'));
    });

    return () => {
      cancelled = true;
    };
  }, [mutate, session?.user]);
}

export async function signIn(credentials: AuthCredentials) {
  const session = await commerceApi.post<AuthSession>('/auth/login', credentials);
  trackCommerceEvent('account_sign_in', {
    role: session.user.role,
  });
  return session;
}

export async function registerAccount(payload: RegisterPayload) {
  const session = await commerceApi.post<AuthSession>('/auth/register', payload);
  trackCommerceEvent('account_register', {
    role: session.user.role,
  });
  return session;
}

export async function sendOtpCode(payload: { email: string; type: OtpType }) {
  return commerceApi.post<{ success: boolean }>('/auth/otp/send', payload);
}

export async function signInWithOtp(payload: { email: string; otp: string }) {
  const session = await commerceApi.post<AuthSession>('/auth/otp/sign-in', payload);
  trackCommerceEvent('account_sign_in', {
    role: session.user.role,
    method: 'otp',
  });
  return session;
}

export async function requestPasswordReset(payload: { email: string; redirectTo?: string }) {
  return commerceApi.post<{ status: boolean; message: string }>('/auth/request-password-reset', payload);
}

export async function resetPassword(payload: { token: string; newPassword: string }) {
  return commerceApi.post<{ status: boolean }>('/auth/reset-password', payload);
}

export async function requestPasswordResetWithOtp(payload: { email: string }) {
  return commerceApi.post<{ success: boolean }>('/auth/otp/request-password-reset', payload);
}

export async function resetPasswordWithOtp(payload: { email: string; otp: string; password: string }) {
  return commerceApi.post<{ success: boolean }>('/auth/otp/reset-password', payload);
}

export async function sendVerificationEmail(payload: { email: string; callbackURL?: string }) {
  return commerceApi.post<{ status: boolean }>('/auth/send-verification-email', payload);
}

export async function verifyEmailToken(payload: { token: string }) {
  return commerceApi.post<{ status: boolean; user: AuthSession['user'] | null }>('/auth/verify-email', payload);
}

export async function verifyEmailWithOtp(payload: { email: string; otp: string }) {
  return commerceApi.post<{ status: boolean; session: AuthSession | null }>('/auth/otp/verify-email', payload);
}

export async function signOut() {
  return commerceApi.post<void>('/auth/logout');
}

export async function updateProfile(payload: Partial<Pick<AccountProfile, 'name' | 'phone' | 'dateOfBirth'>>) {
  return commerceApi.put<{ data: AccountProfile }>('/account/profile', payload);
}

export async function createAddress(payload: Omit<AccountAddress, 'id'>) {
  return commerceApi.post<{ data: AccountAddress }>('/account/addresses', payload);
}

export async function updateAddress(id: string, payload: Partial<Omit<AccountAddress, 'id'>>) {
  return commerceApi.put<{ data: AccountAddress }>(`/account/addresses/${id}`, payload);
}

export async function deleteAddress(id: string) {
  return commerceApi.delete<void>(`/account/addresses/${id}`);
}

export async function addWishlistItem(payload: { productId: string; variantId?: string | null }) {
  return commerceApi.post<{ success: true }>('/account/wishlist', payload);
}

export async function removeWishlistItem(id: string) {
  return commerceApi.delete<void>(`/account/wishlist/${id}`);
}

export async function createInquiry(payload: InquiryPayload) {
  const response = await commerceApi.post<{ data: { id: string; status: string } }>('/inquiries', payload);
  trackCommerceEvent('inquiry_submit', {
    sourceType: payload.sourceType,
    sourceEntityType: payload.sourceEntityType ?? null,
    preferredContactMethod: payload.preferredContactMethod ?? 'whatsapp',
    itemCount: payload.items?.length ?? 0,
  });
  return response;
}

export async function requestInvoiceDownload(id: string) {
  const response = await commerceApi.post<{ data: { url: string; expiresAt: string } }>(
    `/account/invoices/${id}/download`
  );
  trackCommerceEvent('invoice_download', {
    invoiceId: id,
  });
  return response;
}

export function useStorefrontActions() {
  const sessionQuery = useAuthSession();
  const { mutate } = useSWRConfig();

  useWishlistMerge(sessionQuery.data);

  async function saveProduct(product: PublicProduct, variantId?: string | null) {
    if (sessionQuery.data?.user) {
      await addWishlistItem({ productId: product.id, variantId: variantId ?? null });
      await mutate('/account/wishlist');
      trackCommerceEvent('wishlist_add', {
        mode: 'account',
        productId: product.id,
        variantId: variantId ?? null,
      });
      toast.success(`${product.title} added to your wishlist.`);
      return;
    }

    addGuestWishlistItem({ productId: product.id, variantId: variantId ?? null });
    trackCommerceEvent('wishlist_add', {
      mode: 'guest',
      productId: product.id,
      variantId: variantId ?? null,
    });
    toast.success(`${product.title} saved. Sign in later and we will carry it into your wishlist.`);
  }

  async function enquireProductOnWhatsApp(product: PublicProduct, variant?: PublicVariant | null) {
    const message = buildProductInquiryMessage(product, variant);

    await createInquiry({
      sourceType: 'whatsapp',
      sourcePage: `/products/${product.slug}`,
      sourceEntityType: 'product',
      sourceEntityId: product.id,
      preferredContactMethod: 'whatsapp',
      whatsappTarget: '+918870435979',
      items: [
        {
          productId: product.id,
          variantId: variant?.id ?? null,
          quantity: 1,
        },
      ],
      message,
    });

    trackCommerceEvent('whatsapp_click', {
      sourceType: 'product',
      productId: product.id,
      variantId: variant?.id ?? null,
    });
    window.open(buildWhatsAppHref('918870435979', message), '_blank', 'noopener,noreferrer');
  }

  async function enquireCollectionOnWhatsApp(collection: PublicCollection) {
    const message = buildCollectionInquiryMessage(collection);

    await createInquiry({
      sourceType: 'whatsapp',
      sourcePage: `/collections/${collection.slug}`,
      sourceEntityType: 'collection',
      sourceEntityId: collection.id,
      preferredContactMethod: 'whatsapp',
      whatsappTarget: '+918870435979',
      message,
    });

    trackCommerceEvent('whatsapp_click', {
      sourceType: 'collection',
      collectionId: collection.id,
    });
    window.open(buildWhatsAppHref('918870435979', message), '_blank', 'noopener,noreferrer');
  }

  return {
    sessionQuery,
    saveProduct,
    enquireProductOnWhatsApp,
    enquireCollectionOnWhatsApp,
  };
}
